import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import styles from "./SurveyResultPage.module.css";
import api from "../../utils/axiosConfig";

type OptionItem = {
  id: number;
  optionDesc?: string | null;
  isSelected?: boolean;
  answer?: string | null;
};

type QuestionItem = {
  id: number;
  sectionTitle?: string | null;
  order?: number | null;
  question?: string | null;
  hasAnswer?: boolean;
  questionOptionList?: OptionItem[];
};

export default function SurveyResultPage() {
  const { clientId } = useParams();
  const { state } = useLocation() as { state?: { name?: string } };
  const displayName = state?.name ?? "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<QuestionItem[]>([]);
  
  const SECTION_ORDER = [
    "인구학적 특성",
    "건강상태",
    "경제상황",
    "주거환경",
    "일상생활",
    "심리정서",
    "사회적 관계망",
    "욕구",
  ];

  function sectionRank(name: string) {
    const i = SECTION_ORDER.indexOf((name ?? "").trim());
    return i === -1 ? 999 : i; // 모르는 섹션은 뒤로
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/active-data/survey/list/client/${clientId}`);
        let list: QuestionItem[] = [];

        if (Array.isArray(res.data)) {
          list = res.data.flatMap((survey: any) =>
            (survey.questionList ?? []).map((q: any) => ({
              ...q,
              sectionTitle: survey.title, 
            }))
          );
        } else if (res.data?.questionList) {
          list = (res.data.questionList ?? []).map((q: any) => ({
            ...q,
            sectionTitle: res.data.title,  
          }));
        }

        setItems(list);
      } catch (e: any) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "불러오기 실패";
        Swal.fire({ icon: "error", title: "설문 결과 불러오기 실패", text: msg });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId]);

  const grouped = useMemo(() => {
    const map = new Map<string, QuestionItem[]>();
    for (const q of items) {
      const key = (q.sectionTitle ?? "기타").trim();
      const arr = map.get(key) ?? [];
      arr.push(q);
      map.set(key, arr);
    }
    const entries = Array.from(map.entries()).map(([sec, arr]) => {
      const sorted = arr.slice().sort((a, b) => {
        const ao = a.order ?? 0;
        const bo = b.order ?? 0;
        if (ao !== bo) return ao - bo;
        return (a.id ?? 0) - (b.id ?? 0);
      });
      return [sec, sorted] as const;
    });
    entries.sort((a, b) => sectionRank(a[0]) - sectionRank(b[0]));
    return entries;
  }, [items]);

  function toAnswerText(q: QuestionItem): string {
    const opts = q.questionOptionList ?? [];
    if (opts.length === 0) return "미응답";

    // 식사 시간 질문 특별 처리
    const questionText = (q.question ?? "").toLowerCase();
    if (questionText.includes("식사") && questionText.includes("시간")) {
      const mealTypes: string[] = [];
      const timeInputs: string[] = [];
      
      for (const o of opts) {
        if (o.isSelected && o.optionDesc?.trim()) {
          mealTypes.push(o.optionDesc.trim());
        }
        if (o.answer?.trim()) {
          timeInputs.push(o.answer.trim());
        }
      }
      
      // 선택된 식사 종류와 시간을 매칭
      if (mealTypes.length > 0 && timeInputs.length > 0) {
        // 식사 종류와 시간을 짝지어 표시
        const result = mealTypes.map((mealType, index) => {
          const time = timeInputs[index] || timeInputs[0]; // 시간이 부족하면 첫번째 시간 사용
          return `${mealType}(${time})`;
        });
        return result.join(", ");
      } else if (mealTypes.length > 0) {
        return mealTypes.join(", ");
      } else if (timeInputs.length > 0) {
        return timeInputs.join(", ");
      }
    }

    // 돌봄서비스 시간 질문 특별 처리
    if (questionText.includes("돌봄서비스") && questionText.includes("시간")) {
      const serviceTypes: string[] = [];
      const timeInputs: string[] = [];
      
      for (const o of opts) {
        if (o.isSelected && o.optionDesc?.trim()) {
          serviceTypes.push(o.optionDesc.trim());
        }
        if (o.answer?.trim()) {
          timeInputs.push(o.answer.trim());
        }
      }
      
      // 선택된 서비스와 시간을 매칭
      if (serviceTypes.length > 0 && timeInputs.length > 0) {
        // 서비스와 시간을 짝지어 표시
        const result = serviceTypes.map((serviceType, index) => {
          const time = timeInputs[index] || timeInputs[0]; // 시간이 부족하면 첫번째 시간 사용
          return `${serviceType}(${time})`;
        });
        return result.join(", ");
      } else if (serviceTypes.length > 0) {
        return serviceTypes.join(", ");
      } else if (timeInputs.length > 0) {
        return timeInputs.join(", ");
      }
    }

    // 일반 질문 처리 (기존 로직)
    const selectedLabels: string[] = [];
    for (const o of opts) {
      if (o.isSelected && o.optionDesc?.trim()) selectedLabels.push(o.optionDesc.trim());
    }
    for (const o of opts) {
      if (o.answer?.trim()) selectedLabels.push(o.answer.trim());
    }
    if (selectedLabels.length === 0) return "미응답";
    return selectedLabels.join(", ");
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.left}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>← 뒤로</button>
          {displayName && (
            <span className={styles.respondent}>대상자 : {displayName}</span>
          )}
        </div>

        <h2 className={styles.title}>설문 결과</h2>
      </div>

      {loading ? (
        <div className={styles.loading}>불러오는 중…</div>
      ) : grouped.length === 0 ? (
        <div className={styles.empty}>표시할 설문 결과가 없습니다.</div>
      ) : (
        <div className={styles.sections}>
          {grouped.map(([section, list]) => (
            <section key={section} className={styles.section}>
              <h2 className={styles.sectionTitle}>{section}</h2>
              <div className={styles.qaList}>
                {list.map((q, idx) => (
                  <div key={q.id} className={styles.qaItem}>
                    <div className={styles.q}>
                      <span className={styles.qLabel}>{idx + 1}.</span>
                      <span className={styles.qText}>{q.question ?? "-"}</span>
                    </div>
                    <div className={styles.a}>
                      <span className={styles.aLabel}>A.</span>
                      <span
                        className={`${styles.aText} ${toAnswerText(q) === "미응답" ? styles.unanswered : ""}`}
                      >
                        {toAnswerText(q)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}